// src/pages/StartedContribution.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  Alert,
  Snackbar,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  Info,
  EventAvailable,
  Close,
  PlayArrow,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { getData, postData } from "../services/request";

// ✅ Helper: ISO (yyyy-mm-dd) se DMY (dd-mm-yyyy) mein convert karo
const isoToDMY = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}-${month}-${year}`;
};

// ✅ Helper: Date mein din add karke ISO format mein return karo
const addDaysISO = (isoDate, days) => {
  const date = new Date(isoDate);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

// ✅ Helper: ISO se DMY mein format karo
const formatDMY = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getStoredUser = () =>
  JSON.parse(localStorage.getItem("userData") || "{}");
const getUserRole = (user) => user?.userType || "groupUser";

const StartedContribution = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Get Group ID from URL
  const queryParams = new URLSearchParams(location.search);
  const groupId = queryParams.get("groupId");

  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groupData, setGroupData] = useState(null);
  const [members, setMembers] = useState([]);
  const [contributionDate, setContributionDate] = useState(""); // Ab ISO store hoga
  const [collectors, setCollectors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ✅ Modal States
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [startLoading, setStartLoading] = useState(false);

  const user = getStoredUser();
  const role = getUserRole(user);
  const displayName = user?.fullName || user?.full_name || "Dajoo member";

  const goTo = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const logout = () => {
    ["accessToken", "userData", "deviceId", "notifications", "nin"].forEach(
      (key) => localStorage.removeItem(key),
    );
    navigate("/", { replace: true });
  };

  // ✅ Load Group Status & Members
  useEffect(() => {
    const fetchData = async () => {
      if (!groupId) {
        setError("Group ID is missing");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // 1️⃣ Check Group Status
        const statusResponse = await postData("/api/group/group-status", {
          groupId: Number(groupId),
        });

        if (statusResponse?.success === true) {
          const paymentStatus = statusResponse?.payment?.status;

          if (paymentStatus !== "completed") {
            setError(
              statusResponse?.message ||
                "Payment not collected from all members. Please try again later.",
            );
            setLoading(false);
            return;
          }

          // ✅ Status Completed - Store Data
          setGroupData(statusResponse?.data || null);

          // 2️⃣ Fetch Members List
          const membersResponse = await getData(
            `/api/group/members/${groupId}`,
          );
          const membersList = Array.isArray(membersResponse)
            ? membersResponse
            : membersResponse?.data || membersResponse?.members || [];

          setMembers(membersList);

          // ✅ Start Date Auto-Select: Next Day (ISO format mein)
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          setContributionDate(tomorrow.toISOString().split("T")[0]);

          // ✅ Default Collectors Order (Pehle Members ko assign karo)
          setCollectors(
            membersList.map((member) => ({
              userId: String(member.userId || member.User?.id || ""),
              name: member.User?.fullName || member.fullName || "Unknown",
            })),
          );
        } else {
          setError(statusResponse?.message || "Failed to check group status.");
        }
      } catch (error) {
        console.error(error);
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [groupId]);

  // ✅ Handle Collector Change (Dropdown se user select karo)
  const handleCollectorChange = (index, value) => {
    const updatedCollectors = [...collectors];
    const selectedMember = members.find(
      (m) => String(m.userId || m.User?.id) === value,
    );
    updatedCollectors[index] = {
      userId: value,
      name:
        selectedMember?.User?.fullName || selectedMember?.fullName || "Unknown",
    };
    setCollectors(updatedCollectors);
  };

  // ✅ Generate Payload & Submit (Contribution Detail)
  const handleMakeContribution = async () => {
    if (!contributionDate) {
      setSnackbar({
        open: true,
        message: "Please select contribution start date",
        severity: "warning",
      });
      return;
    }

    if (collectors.some((c) => !c.userId)) {
      setSnackbar({
        open: true,
        message: "Please select all collectors",
        severity: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      // ✅ 1st Person Deposit Date = Start Date + 5 days
      const firstDepositISO = addDaysISO(contributionDate, 5);

      // ✅ Baqi Members = 1st person se +30, +60, +90 days
      const membersPayload = collectors.map((collector, index) => {
        let depositDate;
        if (index === 0) {
          depositDate = formatDMY(firstDepositISO); // 1st = 5 days gap
        } else {
          const nextDepositISO = addDaysISO(firstDepositISO, 30 * index);
          depositDate = formatDMY(nextDepositISO); // Baqi = 30 days gap
        }
        return {
          userId: collector.userId,
          name: collector.name,
          dateOfDeposit: depositDate,
        };
      });

      // ✅ Payload: dateOfCollection ab dd-mm-yyyy mein
      const payload = {
        groupId: Number(groupId),
        dateOfCollection: isoToDMY(contributionDate),
        members: membersPayload,
      };

      const response = await postData(
        "/api/group/contribution_detail",
        payload,
      );

      if (response?.status === true || response?.success === true) {
        // ✅ Modal dikhayein
        setSuccessMessage(
          response?.message || "Contribution details saved successfully!",
        );
        setSuccessModalOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: response?.message || "Failed to save contribution details",
          severity: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Network error. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Start Contribution Button (Modal ke andar)
  const handleStartContribution = async () => {
    setStartLoading(true);
    try {
      const payload = {
        groupId: Number(groupId),
      };

      const response = await postData("/api/group/start_contribution", payload);

      if (response?.success === true || response?.status === true) {
        setSnackbar({
          open: true,
          message: response?.message || "Contribution started successfully!",
          severity: "success",
        });
        setSuccessModalOpen(false);
        setTimeout(() => navigate("/started-groups"), 2000);
      } else {
        setSnackbar({
          open: true,
          message: response?.message || "Failed to start contribution",
          severity: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Network error. Please try again.",
        severity: "error",
      });
    } finally {
      setStartLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f6f7fb", color: "#1d2433" }}>
      <Navbar
        onMenuClick={() => setMobileNavOpen(true)}
        onNavigate={goTo}
        onLogout={logout}
        user={{ fullName: displayName, userType: role }}
      />
      <Sidebar
        role={role}
        activePath={location.pathname}
        onNavigate={goTo}
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />
      <Box
        component="main"
        sx={{ ml: { xs: 0, md: "248px" }, p: { xs: 2, sm: 3, md: 5 } }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800 }}
            >
              Contribution Details
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ minHeight: 300, display: "grid", placeItems: "center" }}>
              <CircularProgress sx={{ color: "#52057b" }} />
            </Box>
          ) : error ? (
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                border: "1px solid #e9ebf0",
                borderRadius: 3,
                boxShadow: "none",
              }}
            >
              <Info sx={{ fontSize: 48, color: "#c62828", mb: 2 }} />
              <Typography sx={{ fontWeight: 700, color: "#c62828", mb: 2 }}>
                {error}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/started-groups")}
                sx={{
                  bgcolor: "#52057b",
                  color: "#fff",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Go Back to Started Groups
              </Button>
            </Paper>
          ) : (
            <Paper
              sx={{
                p: { xs: 2.5, sm: 4 },
                border: "1px solid #e9ebf0",
                borderRadius: 3,
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              }}
            >
              {/* Group Payment Status Info */}
              <Box
                sx={{
                  backgroundColor: "#e8f5e9",
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <CheckCircle sx={{ color: "#2e7d32" }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#2e7d32" }}>
                    All payments completed!
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#555" }}>
                    {groupData?.usersCount || 0} members paid. You can now set
                    the contribution order.
                  </Typography>
                </Box>
              </Box>

              {/* Contribution Start Date */}
              <Typography sx={{ fontWeight: 700, color: "#52057b", mb: 1 }}>
                Contribution Start Date
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={contributionDate}
                onChange={(e) => setContributionDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  },
                }}
              />

              {/* Select Collectors */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700, color: "#52057b" }}>
                  Select Order of Collectors (total no. = {collectors.length})
                </Typography>
              </Box>

              {/* Collectors Dropdowns */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {collectors.map((collector, index) => (
                  <Box key={index}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                      {index + 1}
                      {index === 0
                        ? "st"
                        : index === 1
                          ? "nd"
                          : index === 2
                            ? "rd"
                            : "th"}{" "}
                      Collector-
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={collector.userId}
                      onChange={(e) =>
                        handleCollectorChange(index, e.target.value)
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      {members.map((member) => {
                        const memberId = String(
                          member.userId || member.User?.id,
                        );
                        const memberName =
                          member.User?.fullName || member.fullName || "Unknown";
                        return (
                          <MenuItem key={memberId} value={memberId}>
                            {memberName}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Box>
                ))}
              </Box>

              {/* Make Contribution Button */}
              <Box sx={{ mt: 4 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleMakeContribution}
                  disabled={submitting}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <EventAvailable />
                    )
                  }
                  sx={{
                    bgcolor: "#52057b",
                    color: "#fff",
                    borderRadius: "12px",
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "0 6px 20px rgba(82, 5, 123, 0.25)",
                    "&:hover": {
                      bgcolor: "#3e055e",
                    },
                  }}
                >
                  Make Contribution
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      {/* ✅ Success Modal */}
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f7f1fa",
            py: 2,
          }}
        >
          <Typography sx={{ fontWeight: 800, color: "#52057b" }}>
            Success!
          </Typography>
          <IconButton onClick={() => setSuccessModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {successMessage}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={handleStartContribution}
            disabled={startLoading}
            startIcon={
              startLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <PlayArrow />
              )
            }
            sx={{
              bgcolor: "#52057b",
              color: "#fff",
              borderRadius: "12px",
              py: 1.5,
              textTransform: "none",
              fontWeight: 700,
              "&:hover": {
                bgcolor: "#3e055e",
              },
            }}
          >
            Start Contribution
          </Button>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StartedContribution;
